import React, { useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Form, InputGroup } from "react-bootstrap";
import {
  fetchUserVocabulary,
  setSearchTerm,
  hideUserVocabularyWord,
  generateWordSignedUrls,
} from "../../redux/slices/userVocabularySlice";
import { UserContext } from "../user/UserContext";
import handlePronunciation from "../../utils/handlePronunciation";
import VocabularyForm from "./VocabularyForm";

export default function UserVocabulary() {
  const dispatch = useDispatch(); // Initialize the Redux dispatch function
  const { userAttributes } = useContext(UserContext); // Access user attributes from the context
  const wordsSignedUrls = useSelector(
    (state) => state.userVocabulary.wordsSignedUrls
  ); // Get the signed URLs for the words images

  // Extract necessary state values from the Redux store
  const { userWords, loading, searchTerm, nextToken } = useSelector(
    (state) => state.userVocabulary
  );

  // Fetch user vocabulary when the component mounts and whenever the user ID (sub) changes
  useEffect(() => {
    if (userAttributes.sub) {
      const loadData = async () => {
        try {
          console.log("Fetching user vocabulary...");
          // Fetch with forceRefresh to ensure it always fetches fresh data
          const response = await dispatch(
            fetchUserVocabulary({ owner: userAttributes.sub })
          );

          console.log("Fetch response:", response);

          // Check if the payload has words
          if (response.payload && response.payload.items) {
            console.log("Words fetched:", response.payload.items);
            // Generate signed URLs for the word images
            await dispatch(generateWordSignedUrls(response.payload.items));
          }
        } catch (error) {
          console.error("Error in loadData:", error);
        }
      };

      loadData(); // Trigger the async function
    } else {
      console.log("User attributes not found, skipping data load.");
    }
  }, [dispatch, userAttributes.sub]);

  // Fetch additional pages of vocabulary if a nextToken is available
  useEffect(() => {
    if (nextToken) {
      dispatch(
        fetchUserVocabulary({
          owner: userAttributes.sub,
          token: nextToken,
        })
      );
    }
  }, [dispatch, nextToken, userAttributes.sub]);

  // Handle the search input and update the search term in the Redux store
  function handleSearch(event) {
    dispatch(setSearchTerm(event.target.value));
  }

  // Handle the deletion of a word from the user's vocabulary
  function handleDeleteWord(wordId) {
    dispatch(hideUserVocabularyWord({ id: wordId }));
  }

  // Filter the vocabulary words based on the search term
  const getFilteredWords = () => {
    return userWords.filter(
      (word) =>
        word.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
        word.translation.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <Container className="my-3 px-2">
      {/* Search input field */}
      <Form className="mb-3">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search your vocabulary..."
            value={searchTerm}
            onChange={handleSearch}
            aria-label="Search User Vocabulary"
            style={{ height: 42 }}
          />
        </InputGroup>
      </Form>

      {/* Show loading message if vocabulary is still loading */}
      {loading && userWords.length === 0 ? (
        <p>Loading...</p>
      ) : (
        <VocabularyForm
          words={getFilteredWords()}
          wordsSignedUrls={wordsSignedUrls}
          onDeleteWord={handleDeleteWord}
          onPronounceWord={handlePronunciation}
        />
      )}
    </Container>
  );
}
